// src/gestion-logistics/pages/LogisticsTreasury.tsx
import { ensureSubscriptionActionAllowed } from '../../utils/subscriptionActionGuard';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import type { Breakpoint } from "antd/es/_util/responsiveObserver";
import {
  FallOutlined,
  FileExcelOutlined,
  RiseOutlined,
  SwapOutlined,
  UserOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import * as XLSX from "xlsx";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useLogisticsPermissions } from "../hooks/useLogisticsPermissions";
import {
  LogisticsTreasuryEntry,
  LogisticsTreasuryTotals,
} from "../types/treasury.types";
import LogisticsTreasuryAccounts from "./LogisticsTreasuryAccounts";
import LogisticsTreasuryMovements from "./LogisticsTreasuryMovements";
import LogisticsTreasuryTransfers from "./LogisticsTreasuryTransfers";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

const modeTypeLabels: Record<string, string> = {
  cash: "Caisse",
  bank: "Banque",
  mobile: "Mobile",
};

const formatCurrency = (value: number | string, symbol?: string | null) => {
  const numericValue =
    typeof value === "string" ? parseFloat(value) : Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  const formatted = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericValue);
  return symbol ? `${formatted} ${symbol}` : formatted;
};

const formatDateRangeParams = (range: [Dayjs, Dayjs] | null) => {
  if (!range || !range[0] || !range[1]) return null;
  return {
    start_date: range[0].format("YYYY-MM-DD"),
    end_date: range[1].format("YYYY-MM-DD"),
  };
};

const LogisticsTreasury: React.FC = () => {
  const { selectedCompany } = useSelection();
  const permissions = useLogisticsPermissions();
  const [activeView, setActiveView] = useState("flows");
  const [refreshKey, setRefreshKey] = useState(0);

  const [filters, setFilters] = useState<{
    dateRange: [Dayjs, Dayjs] | null;
    paymentType: "all" | "in" | "out";
    modeType: "all" | "cash" | "bank" | "mobile";
    search: string;
  }>({
    dateRange: null,
    paymentType: "all",
    modeType: "all",
    search: "",
  });

  const [data, setData] = useState<LogisticsTreasuryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [totals, setTotals] = useState<LogisticsTreasuryTotals>({
    total_incoming: 0,
    total_outgoing: 0,
    total_count: 0,
    currency_symbol: null,
    currency_count: 0,
  });
  const [exportAll, setExportAll] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const pageTotals = useMemo(() => {
    return data.reduce(
      (acc, item) => {
        const amount = Number(item.amount || 0);
        if (!Number.isFinite(amount)) return acc;
        if (item.payment_type === "in") acc.incoming += amount;
        if (item.payment_type === "out") acc.outgoing += amount;
        return acc;
      },
      { incoming: 0, outgoing: 0 }
    );
  }, [data]);

  const buildBaseParams = useCallback(() => {
    if (!selectedCompany) return null;

    const params: Record<string, string | number> = {
      company_id: selectedCompany,
    };

    const dateParams = formatDateRangeParams(filters.dateRange);
    if (dateParams) {
      params.start_date = dateParams.start_date;
      params.end_date = dateParams.end_date;
    }

    if (filters.paymentType !== "all") {
      params.payment_type = filters.paymentType;
    }

    if (filters.modeType !== "all") {
      params.mode_type = filters.modeType;
    }

    if (filters.search.trim()) {
      params.search = filters.search.trim();
    }

    return params;
  }, [filters, selectedCompany]);

  const fetchTreasury = useCallback(async () => {
    const baseParams = buildBaseParams();
    const params = baseParams
      ? {
          ...baseParams,
          page: pagination.page,
          limit: pagination.limit,
        }
      : null;
    if (!params) {
      setData([]);
      setPagination((prev) => ({ ...prev, total: 0, page: 1 }));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/treasury`,
        { params }
      );

      if (response.data?.success) {
        setData(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination?.total || 0,
        }));
      } else {
        message.error(
          response.data?.message ||
            "Erreur lors du chargement de la tresorerie"
        );
      }
    } catch (error: any) {
      console.error("[LOGISTICS] Error fetching treasury:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement de la tresorerie"
      );
    } finally {
      setLoading(false);
    }
  }, [buildBaseParams, pagination.limit, pagination.page]);

  const fetchTotals = useCallback(async () => {
    const params = buildBaseParams();
    if (!params) {
      setTotals({
        total_incoming: 0,
        total_outgoing: 0,
        total_count: 0,
        currency_symbol: null,
        currency_count: 0,
      });
      return;
    }

    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/logistics/treasury/totals`,
        { params }
      );
      if (response.data?.success) {
        const totalsData = response.data.data || {};
        setTotals({
          total_incoming: Number(totalsData.total_incoming || 0),
          total_outgoing: Number(totalsData.total_outgoing || 0),
          total_count: Number(totalsData.total_count || 0),
          currency_symbol: totalsData.currency_symbol || null,
          currency_count: Number(totalsData.currency_count || 0),
        });
      }
    } catch (error: any) {
      console.error("[LOGISTICS] Error fetching treasury totals:", error);
    }
  }, [buildBaseParams]);

  useEffect(() => {
    if (selectedCompany && activeView === "flows") {
      fetchTreasury();
    }
  }, [activeView, fetchTreasury, selectedCompany]);

  useEffect(() => {
    if (selectedCompany && activeView === "flows") {
      fetchTotals();
    }
  }, [activeView, fetchTotals, selectedCompany]);

  const handleFilterChange = (partial: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      page,
      limit: pageSize,
    }));
  };

  const handleExport = async () => {
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    if (exportLoading) return;

    const baseParams = buildBaseParams();
    const params = baseParams
      ? {
          ...baseParams,
          ...(exportAll
            ? {}
            : { page: pagination.page, limit: pagination.limit }),
        }
      : null;
    if (!params) {
      message.warning("Selectionnez une entreprise pour exporter.");
      return;
    }

    const messageKey = "logistics-treasury-export";
    setExportLoading(true);
    message.loading({
      key: messageKey,
      content: exportAll ? "Export complet en cours..." : "Export de la page...",
    });

    try {
      let exportData = data;
      if (exportAll) {
        const response = await axios.get(
          `${API_CONFIG.API_URL}/logistics/treasury`,
          { params: { ...params, page: 1, limit: 10000 } }
        );
        exportData = response.data?.data || [];
      }

      if (!exportData.length) {
        message.warning({
          key: messageKey,
          content: "Aucune donnee a exporter.",
        });
        return;
      }

      const formatted = exportData.map((item) => ({
        "Date paiement": item.payment_date
          ? dayjs(item.payment_date).format("DD/MM/YYYY")
          : "",
        Type:
          item.payment_type === "in"
            ? "Encaissement"
            : item.payment_type === "out"
            ? "Decaissement"
            : "",
        Source: item.source === "customer" ? "Client" : "Fournisseur",
        "Document N°": item.document_number || "",
        "Type document": item.document_type || "",
        Dossier: item.shipment_number || "",
        Tiers: item.entity_name || "",
        "Mode paiement": item.payment_mode_name || "",
        Montant: Number(item.amount || 0),
        Devise: item.currency_symbol || "",
        "Reference paiement": item.payment_reference || "",
        Notes: item.notes || "",
        "Cree par": item.created_by_name || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(formatted);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Tresorerie");

      const fileName = `Tresorerie_Logistique_${dayjs().format(
        "YYYY-MM-DD_HH-mm"
      )}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success({
        key: messageKey,
        content: "Export termine avec succes.",
      });
    } catch (error: any) {
      console.error("[LOGISTICS] Error exporting treasury:", error);
      message.error({
        key: messageKey,
        content: "Erreur lors de l'export.",
      });
    } finally {
      setExportLoading(false);
    }
  };

  if (!selectedCompany) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour afficher la tresorerie logistique."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  const displayCurrency =
    totals.currency_count && totals.currency_count > 1
      ? null
      : totals.currency_symbol || null;
  const netBalance = totals.total_incoming - totals.total_outgoing;

  const columns = [
    {
      title: "Date",
      dataIndex: "payment_date",
      key: "payment_date",
      width: 110,
      render: (value: string) =>
        value ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Type",
      dataIndex: "payment_type",
      key: "payment_type",
      width: 140,
      render: (value: "in" | "out") =>
        value === "in" ? (
          <Tag color="green">Encaissement</Tag>
        ) : (
          <Tag color="red">Decaissement</Tag>
        ),
    },
    {
      title: "Tiers",
      dataIndex: "entity_name",
      key: "entity_name",
      ellipsis: true,
      render: (value: string, record: LogisticsTreasuryEntry) =>
        value || (record.source === "customer" ? "Client" : "Fournisseur"),
    },
    {
      title: "Document",
      dataIndex: "document_number",
      key: "document_number",
      render: (value: string, record: LogisticsTreasuryEntry) => (
        <Space direction="vertical" size={0}>
          <Text>{value || "-"}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.document_type || ""}
          </Text>
        </Space>
      ),
    },
    {
      title: "Dossier",
      dataIndex: "shipment_number",
      key: "shipment_number",
      ellipsis: true,
    },
    {
      title: "Mode",
      dataIndex: "payment_mode_name",
      key: "payment_mode_name",
      ellipsis: true,
      render: (value: string, record: LogisticsTreasuryEntry) => (
        <Space>
          <Text>{value || "-"}</Text>
          {record.payment_mode_type && (
            <Tag color="blue">
              {modeTypeLabels[record.payment_mode_type] ||
                record.payment_mode_type}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (value: number, record: LogisticsTreasuryEntry) => (
        <Text strong>{formatCurrency(value, record.currency_symbol)}</Text>
      ),
    },
    {
      title: "Reference",
      dataIndex: "payment_reference",
      key: "payment_reference",
      ellipsis: true,
      responsive: ["md"] as Breakpoint[],
      render: (value: string) => value || "-",
    },
    {
      title: "Cree par",
      dataIndex: "created_by_name",
      key: "created_by_name",
      responsive: ["lg"] as Breakpoint[],
      render: (value: string) => value || "-",
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <Title level={3}>Tresorerie Logistique</Title>
        <Text type="secondary">
          Gestion des flux, des comptes et des mouvements financiers lies aux
          dossiers logistiques.
        </Text>
      </div>

      <Tabs activeKey={activeView} onChange={setActiveView} type="card">
        <TabPane tab="Flux factures" key="flows">
          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Encaissements"
                  value={formatCurrency(totals.total_incoming, displayCurrency)}
                  prefix={<RiseOutlined style={{ color: "#52c41a" }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Decaissements"
                  value={formatCurrency(totals.total_outgoing, displayCurrency)}
                  prefix={<FallOutlined style={{ color: "#f5222d" }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Solde net"
                  value={formatCurrency(netBalance, displayCurrency)}
                  prefix={<SwapOutlined style={{ color: "#1890ff" }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Transactions"
                  value={totals.total_count}
                  prefix={<UserOutlined />}
                />
                {totals.currency_count && totals.currency_count > 1 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Totaux multi-devise
                  </Text>
                )}
              </Card>
            </Col>
          </Row>

          <Card className="mb-4">
            <Space
              wrap
              size="middle"
              className="w-full"
              style={{ justifyContent: "space-between" }}
            >
              <Space wrap size="middle">
                <RangePicker
                  value={filters.dateRange || undefined}
                  onChange={(range) =>
                    handleFilterChange({
                      dateRange: range as [Dayjs, Dayjs] | null,
                    })
                  }
                  format="DD/MM/YYYY"
                  allowClear
                />
                <Select
                  value={filters.paymentType}
                  style={{ minWidth: 160 }}
                  onChange={(value) =>
                    handleFilterChange({ paymentType: value })
                  }
                  options={[
                    { value: "all", label: "Tous les flux" },
                    { value: "in", label: "Encaissements" },
                    { value: "out", label: "Decaissements" },
                  ]}
                />
                <Select
                  value={filters.modeType}
                  style={{ minWidth: 160 }}
                  onChange={(value) => handleFilterChange({ modeType: value })}
                  options={[
                    { value: "all", label: "Tous modes" },
                    { value: "cash", label: "Caisse" },
                    { value: "bank", label: "Banque" },
                    { value: "mobile", label: "Mobile money" },
                  ]}
                />
                <Input.Search
                  placeholder="Rechercher dossier, document, tiers..."
                  allowClear
                  onSearch={(value) => handleFilterChange({ search: value })}
                  onChange={(event) => {
                    if (!event.target.value) {
                      handleFilterChange({ search: "" });
                    }
                  }}
                  style={{ minWidth: 240 }}
                />
              </Space>
              <Space>
                <Checkbox
                  checked={exportAll}
                  onChange={(event) => setExportAll(event.target.checked)}
                >
                  Export complet
                </Checkbox>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExport}
                  loading={exportLoading}
                  disabled={!permissions.canExportTreasury}
                >
                  Exporter
                </Button>
              </Space>
            </Space>
          </Card>

          <Card>
            <Table
              dataSource={data}
              columns={columns}
              rowKey={(record) => `${record.source}-${record.id}`}
              loading={loading}
              pagination={{
                current: pagination.page,
                pageSize: pagination.limit,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                onChange: handleTableChange,
              }}
              scroll={{ x: "max-content" }}
            />
            <Text type="secondary" className="mt-2 block">
              Totaux page:{" "}
              {formatCurrency(pageTotals.incoming, displayCurrency)} en entree,{" "}
              {formatCurrency(pageTotals.outgoing, displayCurrency)} en sortie.
              Resultats: {data.length} / {pagination.total}.
            </Text>
          </Card>
        </TabPane>
        {permissions.canViewTreasuryAccounts && (
          <TabPane tab="Comptes" key="accounts">
            <LogisticsTreasuryAccounts refreshKey={refreshKey} moduleScope="logistics" />
          </TabPane>
        )}
        {permissions.canViewTreasuryMovements && (
          <TabPane tab="Mouvements" key="movements">
            <LogisticsTreasuryMovements
              active={activeView === "movements"}
              refreshKey={refreshKey}
              moduleScope="logistics"
            />
          </TabPane>
        )}
        {permissions.canCreateTreasuryTransfers && (
          <TabPane tab="Transferts" key="transfers">
            <LogisticsTreasuryTransfers
              onTransferCompleted={() => setRefreshKey((prev) => prev + 1)}
              moduleScope="logistics"
            />
          </TabPane>
        )}
      </Tabs>
    </div>
  );
};

export default LogisticsTreasury;
